import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * ThemeProvider — the on/off machinery for dark mode.
 *
 * `apps/client/design/tokens.css` already ships a full `.dark` cascade, but
 * nothing in the running app ever toggled the `dark` class. This provider is
 * that missing switch: it resolves the user's preference (light / dark /
 * follow-the-OS), persists it, and reflects it onto `<html class="dark">` so
 * the existing token cascade lights up. It deliberately changes **no token
 * values** — that's a follow-on design pass's job. When the dark theme's
 * values land in `tokens.css`, this provider already lights them up with no
 * further wiring.
 *
 * The pre-paint half of this logic is duplicated as an inline script in
 * `apps/client/index.html` (FOUC prevention — the class must be set before
 * first paint so dark users don't flash white). The storage key and the
 * resolution rule here MUST stay identical to that script so React's first
 * commit agrees with what the inline script already painted (no second-paint
 * flip).
 */

export type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

/** localStorage key. Identical to the inline FOUC script in `index.html`. */
const STORAGE_KEY = 'str-theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

interface ThemeContextValue {
  /** The user's stored preference: light / dark / system (default system). */
  theme: Theme;
  /** Set the preference. Persists to localStorage and re-applies the class. */
  setTheme: (theme: Theme) => void;
  /** The concrete theme after resolving `system` against the OS preference. */
  resolvedTheme: ResolvedTheme;
}

// Exported so non-app hosts that can't run the full provider (Storybook, which
// drives the `.dark` class via its own theme-toolbar decorator) can supply a
// passive context value to satisfy `useTheme()` without a second class-writer
// fighting the toolbar. The app itself always uses <ThemeProvider>.
export const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Read the stored preference. Wrapped in try/catch because localStorage can
 * throw in privacy-mode browsers or be absent in non-browser runtimes — the
 * same storage-disabled posture the auth hints use. Falls back to `system`,
 * which matches the inline script's `|| 'system'` default.
 */
function readStoredTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // Storage blocked / unavailable — fall through to the default.
  }
  return 'system';
}

/** Does the OS currently prefer dark? Defends against jsdom / no-matchMedia. */
function systemPrefersDark(): boolean {
  try {
    return window.matchMedia(DARK_QUERY).matches;
  } catch {
    return false;
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  // The OS's current dark preference. The ONLY reactive state besides `theme`:
  // a `system` user needs a re-render when the OS flips. `resolvedTheme` is
  // derived from `theme` + `systemDark` during render — no second state slot,
  // no effect that just mirrors derived state (see
  // react.dev/learn/you-might-not-need-an-effect, enforced by the
  // react-hooks/set-state-in-effect lint rule).
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  const resolvedTheme: ResolvedTheme =
    theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  // Apply the resolved theme to <html> — this is the external-system write the
  // effect is *for*. Runs whenever the resolution changes.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolvedTheme === 'dark');
  }, [resolvedTheme]);

  // Track OS appearance for the whole provider lifetime (not just while on
  // `system`) so there's no resubscribe gap and the effect body only registers
  // a listener — no synchronous setState. `systemDark` only affects render when
  // `theme === 'system'`, so tracking it always is harmless. The listener
  // callback is the sanctioned setState path (external system → React).
  useEffect(() => {
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(DARK_QUERY);
    } catch {
      // No matchMedia (e.g. jsdom without a stub) — nothing to subscribe to.
      return;
    }

    const onChange = (event: MediaQueryListEvent) => {
      setSystemDark(event.matches);
    };

    mql.addEventListener('change', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage blocked — the preference still applies for this session via
      // the effects above; it just won't survive a reload. Degraded, not broken.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, resolvedTheme }),
    [theme, setTheme, resolvedTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

/**
 * Read the current theme + setter. Must be called inside a `<ThemeProvider>`;
 * throws otherwise so a missing provider surfaces at the call site rather than
 * silently reading a default.
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) {
    throw new Error('useTheme must be used within a <ThemeProvider>');
  }
  return ctx;
}

/** The localStorage key, exported for tests + the FOUC-script parity check. */
export const THEME_STORAGE_KEY = STORAGE_KEY;
